/**
 * useShiftsActions — Save/Delete/Edit-Handler für Shifts, Maßnahmen, Activities + Drag&Drop + Import.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { arrayMove } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import { shiftsAPI, massnahmenAPI, activitiesAPI } from '../../lib/api';

export function useShiftsActions({
  shifts, massnahmen, setMassnahmen,
  selectedLocationId,
  loadShifts, loadMassnahmen, loadBaseData,
  shiftCounts = {},
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [massnahmeDialogOpen, setMassnahmeDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedMassnahme, setSelectedMassnahme] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Delete-Confirm-Dialog für Maßnahmen (inkl. "Trotzdem löschen" bei vorhandenen Einsätzen)
  const [deleteMassnahmeDialog, setDeleteMassnahmeDialog] = useState({
    open: false,
    massnahme: null,
    shiftsCount: 0,
    deleting: false,
  });

  const [formData, setFormData] = useState({
    employee_id: '', honorarkraft_id: '', person_type: 'mitarbeiter',
    massnahme_id: '', location_id: '', room_id: '',
    date: '', start_time: '', end_time: '', notes: '', position_override: '',
  });
  const [massnahmeForm, setMassnahmeForm] = useState({
    name: '', short_code: '', location_id: '', color: '#3B82F6', description: '',
    teilnehmer_anzahl: '', start_date: '', end_date: '', start_time: '', end_time: '',
    excluded_weeks: [], scheduled_weeks: [], is_active: true,
  });
  const [activityForm, setActivityForm] = useState({
    massnahme_id: '', location_id: '', date: '', activity_type: 'berufsschule',
    custom_label: '', notes: '', participant_count: '',
    start_time: '', end_time: '',
  });
  const [recurrence, setRecurrence] = useState({ mode: 'once', end_date: '' });
  const [activityRecurrence, setActivityRecurrence] = useState({ mode: 'once', end_date: '', skip_holidays: true });
  // Mehrtages-Modus für „Neuer Einsatz": ausgewählte konkrete Tage (YYYY-MM-DD).
  const [multiDayDays, setMultiDayDays] = useState([]);
  // Mehrtages-Modus für „Neue Aktivität".
  const [activityMultiDayDays, setActivityMultiDayDays] = useState([]);

  // ---- Shift CRUD ----
  const openNewShiftDialog = (date, massnahmeId = '') => {
    setSelectedShift(null);
    const selectedMassnahmeForShift = massnahmeId ? massnahmen.find(m => m.id === massnahmeId) : (massnahmen[0] || null);
    const defaultLocationId = selectedMassnahmeForShift?.location_id || (selectedLocationId !== 'all' ? selectedLocationId : '');
    setFormData({
      employee_id: '',
      honorarkraft_id: '',
      person_type: 'mitarbeiter',
      massnahme_id: massnahmeId || (massnahmen[0]?.id ?? ''),
      location_id: defaultLocationId,
      room_id: '',
      date: format(date, 'yyyy-MM-dd'),
      start_time: '',
      end_time: '',
      notes: '',
      position_override: '',
    });
    setRecurrence({ mode: 'once', end_date: '' });
    setMultiDayDays([]);
    setDialogOpen(true);
  };

  const openEditShiftDialog = (shift) => {
    setSelectedShift(shift);
    setFormData({
      employee_id: shift.employee_id || '',
      honorarkraft_id: shift.honorarkraft_id || '',
      person_type: shift.is_honorarkraft ? 'honorarkraft' : 'mitarbeiter',
      massnahme_id: shift.massnahme_id,
      location_id: shift.location_id || '',
      room_id: shift.room_id || '',
      date: shift.date,
      start_time: shift.start_time || '',
      end_time: shift.end_time || '',
      notes: shift.notes || '',
      position_override: shift.position_override || '',
    });
    setDialogOpen(true);
  };

  const handleSaveShift = async () => {
    const isHk = formData.person_type === 'honorarkraft';
    const actorId = isHk ? formData.honorarkraft_id : formData.employee_id;
    if (!actorId || !formData.massnahme_id || !formData.date) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    // Start-/Endzeit sind optional (2026-05-08): Einsätze dürfen ohne feste
    // Uhrzeiten angelegt werden — z.B. für ganztägige Aktivitäten oder wenn
    // die Zeit später nachgepflegt wird.
    setSaving(true);
    try {
      const payload = {
        ...formData,
        employee_id: isHk ? null : formData.employee_id,
        honorarkraft_id: isHk ? formData.honorarkraft_id : null,
        location_id: formData.location_id || null,
        room_id: formData.room_id || null,
        position_override: formData.position_override || null,
      };
      if (selectedShift) {
        const upd = await shiftsAPI.update(selectedShift.id, payload);
        if (upd.data?.room_warning) {
          toast.warning(`Einsatz aktualisiert – Hinweis: ${upd.data.room_warning}`);
        } else {
          toast.success('Einsatz aktualisiert');
        }
      } else if (multiDayDays.length > 0) {
        // Mehrtages-Modus: Einsatz an jedem ausgewählten Tag anlegen.
        let successCount = 0;
        let conflictCount = 0;
        const failures = [];
        const sortedDays = [...multiDayDays].sort();
        for (const day of sortedDays) {
          try {
            const res = await shiftsAPI.create({ ...payload, date: day });
            if (res.data?.has_conflict) conflictCount++;
            successCount++;
          } catch (err) {
            failures.push({ date: day, msg: err.response?.data?.detail || 'Fehler' });
          }
        }
        if (successCount > 0 && failures.length === 0) {
          if (conflictCount > 0) {
            toast.warning(`${successCount} Einsätze erstellt (${conflictCount} mit Konflikten)`);
          } else {
            toast.success(`${successCount} Einsätze erstellt`);
          }
        } else if (successCount > 0) {
          toast.warning(`${successCount} Einsätze erstellt, ${failures.length} fehlgeschlagen`);
        } else {
          toast.error(`Keine Einsätze erstellt — ${failures.length} fehlgeschlagen`);
        }
      } else if (recurrence.mode !== 'once') {
        if (!recurrence.end_date) {
          toast.error('Bitte Enddatum der Serie angeben');
          setSaving(false);
          return;
        }
        const seriesPayload = {
          employee_id: isHk ? null : formData.employee_id,
          honorarkraft_id: isHk ? formData.honorarkraft_id : null,
          massnahme_id: formData.massnahme_id,
          location_id: formData.location_id || null,
          room_id: formData.room_id || null,
          start_date: formData.date,
          end_date: recurrence.end_date,
          start_time: formData.start_time || null,
          end_time: formData.end_time || null,
          notes: formData.notes || null,
          pattern: recurrence.mode,
          skip_conflicts: true,
          position_override: formData.position_override || null,
        };
        const seriesRes = await shiftsAPI.createSeries(seriesPayload);
        const { created, skipped, holiday_skipped: holidaySkipped = 0, closure_skipped: closureSkipped = 0, room_warnings: roomWarnings = [] } = seriesRes.data || {};
        const extraNote = [
          holidaySkipped > 0 ? `${holidaySkipped} Feiertag(e) ausgelassen` : '',
          closureSkipped > 0 ? `${closureSkipped} Schließtag(e) ausgelassen` : '',
        ].filter(Boolean).join(', ');
        const holidayNote = extraNote ? `, ${extraNote}` : '';
        if (created === 0) {
          if (skipped > 0) {
            toast.warning(`Keine Einsätze erstellt — an allen ${skipped} Tagen besteht bereits ein Einsatz in dieser Maßnahme${holidayNote}`);
          } else if (holidaySkipped > 0 || closureSkipped > 0) {
            toast.warning(`Keine Einsätze erstellt — alle Tage im Zeitraum sind Feier-/Schließtage (${holidaySkipped + closureSkipped})`);
          } else {
            toast.warning('Keine Einsätze erstellt');
          }
        } else if (skipped > 0 || holidaySkipped > 0 || closureSkipped > 0) {
          const parts = [];
          if (skipped > 0) parts.push(`${skipped} Tage übersprungen (bereits belegt)`);
          if (holidaySkipped > 0) parts.push(`${holidaySkipped} Feiertag(e) ausgelassen`);
          if (closureSkipped > 0) parts.push(`${closureSkipped} Schließtag(e) ausgelassen`);
          toast.success(`${created} Einsätze erstellt — ${parts.join(', ')}`);
        } else {
          toast.success(`${created} Einsätze als Serie erstellt`);
        }
        if (roomWarnings.length > 0) {
          toast.warning(`Hinweis: Raum an ${roomWarnings.length} Tag(en) bereits in der Einsatzplanung belegt.`);
        }
      } else {
        const response = await shiftsAPI.create(payload);
        if (response.data.room_warning) {
          toast.warning(`Einsatz erstellt – Hinweis: ${response.data.room_warning}`);
        } else if (response.data.has_conflict) {
          toast.warning('Einsatz erstellt - Warnung: Mitarbeiter ist am selben Tag bereits in einer anderen Maßnahme eingeteilt!');
        } else {
          toast.success('Einsatz erstellt');
        }
      }
      setDialogOpen(false);
      loadShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Einsatz wirklich löschen?')) return;
    try {
      await shiftsAPI.delete(shiftId);
      toast.success('Einsatz gelöscht');
      loadShifts();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteSeries = async (seriesId) => {
    if (!seriesId) return;
    let count = 0;
    try {
      const res = await shiftsAPI.getSeries(seriesId);
      count = res.data?.count || 0;
    } catch (error) {
      // Serie bereits weg — fallback
    }
    const msg = count > 0
      ? `Diese Serie umfasst ${count} Einsätze. Alle löschen?`
      : 'Alle Einsätze dieser Serie löschen?';
    if (!window.confirm(msg)) return;
    try {
      const res = await shiftsAPI.deleteSeries(seriesId);
      toast.success(`${res.data?.deleted ?? count} Einsätze gelöscht`);
      setDialogOpen(false);
      loadShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen der Serie');
    }
  };

  const handleShiftDrop = async (shiftId, newDate, newMassnahmeId = null) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return;
    const isCrossMeasure = !!newMassnahmeId && newMassnahmeId !== shift.massnahme_id;
    const payload = { ...shift, date: newDate };
    if (isCrossMeasure) {
      payload.massnahme_id = newMassnahmeId;
    }
    try {
      await shiftsAPI.update(shiftId, payload);
      if (isCrossMeasure) {
        const targetName = massnahmen.find((m) => m.id === newMassnahmeId)?.name || 'andere Maßnahme';
        toast.success(`Einsatz nach „${targetName}" verschoben`);
      } else {
        toast.success('Einsatz verschoben');
      }
      loadShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verschieben');
    }
  };

  // Activity D&D: identisch zur Shift-Variante, nutzt `activitiesAPI.update`.
  const handleActivityDrop = async (activityId, newDate, newMassnahmeId = null) => {
    const update = { date: newDate };
    let isCrossMeasure = false;
    if (newMassnahmeId) {
      // Original-Aktivität finden, um Cross-Measure-Status zu bestimmen.
      // shifts-Hook hält activities nicht; wir vergleichen einfach optimistisch:
      // sendet immer die neue massnahme_id, Backend macht den Rest.
      update.massnahme_id = newMassnahmeId;
      isCrossMeasure = true;
    }
    try {
      await activitiesAPI.update(activityId, update);
      if (isCrossMeasure) {
        const targetName = massnahmen.find((m) => m.id === newMassnahmeId)?.name || 'andere Maßnahme';
        toast.success(`Aktivität nach „${targetName}" verschoben`);
      } else {
        toast.success('Aktivität verschoben');
      }
      loadShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verschieben');
    }
  };

  // ---- Maßnahme CRUD ----
  const openNewMassnahmeDialog = () => {
    setSelectedMassnahme(null);
    setMassnahmeForm({
      name: '', short_code: '',
      location_id: selectedLocationId !== 'all' ? selectedLocationId : '',
      color: '#3B82F6', description: '', teilnehmer_anzahl: '',
      start_date: '', end_date: '', start_time: '', end_time: '',
      weekday_times: null,
      excluded_weeks: [], scheduled_weeks: [], parent_measure_ids: [], is_active: true, hide_in_tv: false,
      pause_minutes: 45, weekday_pauses: null,
    });
    setMassnahmeDialogOpen(true);
  };

  const openEditMassnahmeDialog = (massnahme) => {
    setSelectedMassnahme(massnahme);
    setMassnahmeForm({
      name: massnahme.name,
      short_code: massnahme.short_code,
      location_id: massnahme.location_id || '',
      color: massnahme.color,
      description: massnahme.description || '',
      teilnehmer_anzahl: massnahme.teilnehmer_anzahl || '',
      start_date: massnahme.start_date || '',
      end_date: massnahme.end_date || '',
      start_time: massnahme.start_time || '',
      end_time: massnahme.end_time || '',
      weekday_times: massnahme.weekday_times || null,
      excluded_weeks: massnahme.excluded_weeks || [],
      scheduled_weeks: massnahme.scheduled_weeks || [],
      parent_measure_ids: massnahme.parent_measure_ids || [],
      hide_in_tv: !!massnahme.hide_in_tv,
      is_active: massnahme.is_active,
      pause_minutes: massnahme.pause_minutes ?? '',
      // Leeres Objekt {} bedeutet „keine Wochentags-Pausen" -> als null laden,
      // damit der PauseEditor die Standard-Pause (pause_minutes) anzeigt und
      // nicht faelschlich in den „je Wochentag"-Modus springt.
      weekday_pauses:
        massnahme.weekday_pauses && Object.keys(massnahme.weekday_pauses).length > 0
          ? massnahme.weekday_pauses
          : null,
    });
    setMassnahmeDialogOpen(true);
  };

  const handleSaveMassnahme = async () => {
    if (!massnahmeForm.name || !massnahmeForm.short_code || !massnahmeForm.location_id) {
      toast.error('Name, Kürzel und Standort sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...massnahmeForm,
        teilnehmer_anzahl: massnahmeForm.teilnehmer_anzahl ? parseInt(massnahmeForm.teilnehmer_anzahl) : null,
        pause_minutes: massnahmeForm.pause_minutes === '' || massnahmeForm.pause_minutes == null
          ? 0 : parseInt(massnahmeForm.pause_minutes),
      };
      // Wochentags-Pausen bereinigen: leere Zellen entfernen, Rest als Ganzzahl.
      // Bei deaktiviert ({}/null) ein leeres Objekt senden, damit ein zuvor
      // gesetzter Wert auch wieder entfernt werden kann (Update filtert null).
      if (payload.weekday_pauses && typeof payload.weekday_pauses === 'object') {
        const cleaned = {};
        Object.entries(payload.weekday_pauses).forEach(([k, v]) => {
          if (v !== '' && v != null) cleaned[k] = parseInt(v, 10) || 0;
        });
        payload.weekday_pauses = cleaned;
      } else {
        payload.weekday_pauses = {};
      }
      if (selectedMassnahme) {
        await massnahmenAPI.update(selectedMassnahme.id, payload);
        toast.success('Maßnahme aktualisiert');
      } else {
        await massnahmenAPI.create(payload);
        toast.success('Maßnahme erstellt');
      }
      setMassnahmeDialogOpen(false);
      loadMassnahmen();
      loadBaseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMassnahme = (massnahme) => {
    // Vorab-Zähler: bevorzugt aus den geladenen Gesamt-Einsatzzählern
    // (shiftCounts), sonst aus dem lokalen shifts-Array. Der Server-Response
    // korrigiert die Zahl beim ersten Delete-Versuch falls nötig.
    const linkedShifts = (shiftCounts && shiftCounts[massnahme.id] != null)
      ? shiftCounts[massnahme.id]
      : (shifts?.filter?.((s) => s.massnahme_id === massnahme.id)?.length || 0);
    setDeleteMassnahmeDialog({
      open: true,
      massnahme,
      shiftsCount: linkedShifts,
      deleting: false,
    });
  };

  const confirmDeleteMassnahme = async () => {
    const { massnahme, shiftsCount } = deleteMassnahmeDialog;
    if (!massnahme) return;
    setDeleteMassnahmeDialog((p) => ({ ...p, deleting: true }));
    try {
      const force = shiftsCount > 0;
      const res = await massnahmenAPI.delete(massnahme.id, { force });
      toast.success(
        res.data?.deleted_shifts
          ? `Maßnahme inkl. ${res.data.deleted_shifts} Einsätzen gelöscht`
          : 'Maßnahme gelöscht'
      );
      setDeleteMassnahmeDialog({ open: false, massnahme: null, shiftsCount: 0, deleting: false });
      loadMassnahmen();
      loadShifts();
      loadBaseData();
    } catch (error) {
      const resp = error.response;
      // Falls Server meldet, dass Einsätze vorhanden sind → Dialog auf Force-Modus umschalten
      if (resp?.status === 409 && resp?.data?.detail?.can_force) {
        setDeleteMassnahmeDialog((p) => ({
          ...p,
          shiftsCount: resp.data.detail.shifts_count || 0,
          deleting: false,
        }));
        return;
      }
      toast.error(resp?.data?.detail?.message || resp?.data?.detail || 'Fehler beim Löschen');
      setDeleteMassnahmeDialog((p) => ({ ...p, deleting: false }));
    }
  };

  const closeDeleteMassnahmeDialog = () => {
    setDeleteMassnahmeDialog({ open: false, massnahme: null, shiftsCount: 0, deleting: false });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id === over?.id) return;

    // Optimistisches lokales Update + Server-Persistierung
    let nextItems = null;
    let prevItems = null;
    setMassnahmen((items) => {
      prevItems = items;
      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      nextItems = arrayMove(items, oldIndex, newIndex);
      return nextItems;
    });

    try {
      await massnahmenAPI.reorder(nextItems.map((m) => m.id));
      toast.success('Reihenfolge gespeichert');
    } catch (err) {
      // Rollback
      if (prevItems) setMassnahmen(prevItems);
      toast.error(err.response?.data?.detail || 'Reihenfolge konnte nicht gespeichert werden');
    }
  };

  // ---- Import / Template ----
  const handleDownloadTemplate = async () => {
    try {
      const response = await massnahmenAPI.exportTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'massnahmen_vorlage.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Vorlage heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen der Vorlage');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const response = await massnahmenAPI.import(file);
      const { imported, skipped, errors, total_errors } = response.data;
      if (imported > 0) {
        toast.success(`${imported} Maßnahme(n) importiert`);
        loadMassnahmen();
      }
      if (skipped > 0) toast.warning(`${skipped} Zeile(n) übersprungen`);
      if (errors && errors.length > 0) {
        errors.slice(0, 3).forEach((err) => toast.error(err));
        if (total_errors > 3) toast.error(`... und ${total_errors - 3} weitere Fehler`);
      }
      setImportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // ---- Activity CRUD ----
  const openActivityDialog = (day, massnahmeId) => {
    let locationId = selectedLocationId !== 'all' ? selectedLocationId : '';
    if (massnahmeId) {
      const massnahme = massnahmen.find(m => m.id === massnahmeId);
      if (massnahme?.location_id) locationId = massnahme.location_id;
    }
    setSelectedActivity(null);
    setActivityForm({
      massnahme_id: massnahmeId || '',
      location_id: locationId,
      date: day instanceof Date ? format(day, 'yyyy-MM-dd') : '',
      activity_type: 'berufsschule',
      custom_label: '',
      notes: '',
      participant_count: '',
      start_time: '',
      end_time: '',
    });
    setActivityRecurrence({ mode: 'once', end_date: '', skip_holidays: true });
    setActivityMultiDayDays([]);
    setActivityDialogOpen(true);
  };

  const openEditActivityDialog = (activity) => {
    setSelectedActivity(activity);
    setActivityForm({
      massnahme_id: activity.massnahme_id || '',
      location_id: activity.location_id || '',
      date: activity.date || '',
      activity_type: activity.activity_type || 'berufsschule',
      custom_label: activity.custom_label || '',
      notes: activity.notes || '',
      participant_count: activity.participant_count != null ? String(activity.participant_count) : '',
      start_time: activity.start_time || '',
      end_time: activity.end_time || '',
    });
    // Beim Bearbeiten keine Serien-/Multi-Day-Optionen anbieten.
    setActivityRecurrence({ mode: 'once', end_date: '', skip_holidays: true });
    setActivityMultiDayDays([]);
    setActivityDialogOpen(true);
  };

  const handleSaveActivity = async () => {
    if (!activityForm.massnahme_id || !activityForm.date) {
      toast.error('Maßnahme und Datum sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      let locationId = activityForm.location_id;
      if (!locationId) {
        const massnahme = massnahmen.find(m => m.id === activityForm.massnahme_id);
        locationId = massnahme?.location_id || '';
      }
      if (!locationId) {
        toast.error('Kein Standort für diese Aktivität gefunden');
        setSaving(false);
        return;
      }
      if (activityMultiDayDays.length > 0) {
        // Mehrtages-Modus: Aktivität an jedem ausgewählten Tag anlegen.
        let successCount = 0;
        const failures = [];
        const sortedDays = [...activityMultiDayDays].sort();
        const basePayload = {
          ...activityForm,
          location_id: locationId,
          participant_count: activityForm.participant_count ? parseInt(activityForm.participant_count) : null,
          start_time: activityForm.start_time || null,
          end_time: activityForm.end_time || null,
        };
        for (const day of sortedDays) {
          try {
            await activitiesAPI.create({ ...basePayload, date: day });
            successCount++;
          } catch (err) {
            failures.push({ date: day, msg: err.response?.data?.detail || 'Fehler' });
          }
        }
        if (successCount > 0 && failures.length === 0) {
          toast.success(`${successCount} Aktivitäten erstellt`);
        } else if (successCount > 0) {
          toast.warning(`${successCount} Aktivitäten erstellt, ${failures.length} fehlgeschlagen`);
        } else {
          toast.error(`Keine Aktivitäten erstellt — ${failures.length} fehlgeschlagen`);
        }
      } else if (activityRecurrence.mode !== 'once') {
        if (!activityRecurrence.end_date) {
          toast.error('Bitte Enddatum der Serie angeben');
          setSaving(false);
          return;
        }
        const seriesPayload = {
          massnahme_id: activityForm.massnahme_id,
          location_id: locationId,
          activity_type: activityForm.activity_type,
          custom_label: activityForm.custom_label || null,
          notes: activityForm.notes || null,
          participant_count: activityForm.participant_count ? parseInt(activityForm.participant_count) : null,
          start_time: activityForm.start_time || null,
          end_time: activityForm.end_time || null,
          start_date: activityForm.date,
          end_date: activityRecurrence.end_date,
          pattern: activityRecurrence.mode,
          skip_existing: true,
          skip_holidays: activityRecurrence.skip_holidays !== false,
        };
        const res = await activitiesAPI.createSeries(seriesPayload);
        const { created, skipped, skipped_holiday } = res.data || {};
        if (created === 0) {
          toast.warning(`Keine Aktivitäten erstellt — alle ${skipped + (skipped_holiday || 0)} Tage übersprungen`);
        } else {
          const parts = [`${created} erstellt`];
          if (skipped) parts.push(`${skipped} bereits belegt`);
          if (skipped_holiday) parts.push(`${skipped_holiday} Ferientage`);
          toast.success(parts.join(' · '));
        }
      } else {
        const payload = {
          ...activityForm,
          location_id: locationId,
          participant_count: activityForm.participant_count ? parseInt(activityForm.participant_count) : null,
          start_time: activityForm.start_time || null,
          end_time: activityForm.end_time || null,
        };
        if (selectedActivity) {
          await activitiesAPI.update(selectedActivity.id, payload);
          toast.success('Aktivität aktualisiert');
        } else {
          await activitiesAPI.create(payload);
          toast.success('Aktivität erstellt');
        }
      }
      setActivityDialogOpen(false);
      setSelectedActivity(null);
      loadShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Aktivität wirklich löschen?')) return;
    try {
      await activitiesAPI.delete(activityId);
      toast.success('Aktivität gelöscht');
      loadShifts();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  /**
   * Bewegt einen Eintrag (Shift oder Activity) innerhalb seiner Tageszelle
   * eine Position nach oben oder unten.
   *
   * @param {string} entryId     ID des zu verschiebenden Eintrags
   * @param {Array}  cellItems   Aktuelle (sortierte) Liste der Eintraege im
   *                             gleichen Zellen-Kontext (Massnahme + Tag),
   *                             gemischt aus Shifts + Activities. Jedes Item
   *                             muss `{id, _type: "shift"|"activity"}` haben.
   * @param {number} delta       -1 = nach oben, +1 = nach unten
   *
   * Es wird ein neues `sort_order` (0..n-1) fuer alle Eintraege gesetzt und
   * per PUT an das Backend gespielt — robust auch wenn vorher noch keine
   * `sort_order` gesetzt waren. Nur tatsaechlich geaenderte Eintraege werden
   * gepusht, um Roundtrips zu sparen.
   */
  const reorderEntryInCell = async (entryId, cellItems, delta) => {
    const idx = cellItems.findIndex((it) => it.id === entryId);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= cellItems.length) return;

    const reordered = arrayMove(cellItems, idx, newIdx);
    const updates = [];
    reordered.forEach((item, newOrder) => {
      // nur Eintraege updaten, deren neue Position sich von der alten unterscheidet
      // bzw. die noch gar keine sort_order haben (-> initial befuellen).
      if (item.sort_order !== newOrder) {
        updates.push({ ...item, sort_order: newOrder });
      }
    });
    if (!updates.length) return;

    try {
      await Promise.all(updates.map((u) => {
        if (u._type === 'shift') {
          return shiftsAPI.update(u.id, { sort_order: u.sort_order });
        }
        return activitiesAPI.update(u.id, { sort_order: u.sort_order });
      }));
      loadShifts();
    } catch (err) {
      toast.error('Reihenfolge konnte nicht gespeichert werden');
    }
  };

  /**
   * Reorder bei Drag&Drop AUF einen anderen Eintrag in derselben Tageszelle.
   * draggedItem wird VOR targetItem positioniert.
   *
   * @param {object} draggedItem  {id, _type: "shift"|"activity"}
   * @param {object} targetItem   {id, _type: "shift"|"activity"}
   * @param {Array}  cellItems    Aktuelle (sortierte) Liste der Eintraege
   *                              im gleichen Massnahme/Tag-Kontext.
   */
  const reorderEntryBeforeTarget = async (draggedItem, targetItem, cellItems) => {
    if (!draggedItem || !targetItem || draggedItem.id === targetItem.id) return;
    const fromIdx = cellItems.findIndex((it) => it.id === draggedItem.id);
    const toIdx = cellItems.findIndex((it) => it.id === targetItem.id);
    if (fromIdx < 0 || toIdx < 0) return;
    // arrayMove sorgt fuer den richtigen Index, auch wenn von oben nach unten
    // bzw. von unten nach oben gezogen wird.
    const reordered = arrayMove(cellItems, fromIdx, toIdx);
    const updates = [];
    reordered.forEach((item, newOrder) => {
      if (item.sort_order !== newOrder) {
        updates.push({ ...item, sort_order: newOrder });
      }
    });
    if (!updates.length) return;
    try {
      await Promise.all(updates.map((u) => {
        if (u._type === 'shift') {
          return shiftsAPI.update(u.id, { sort_order: u.sort_order });
        }
        return activitiesAPI.update(u.id, { sort_order: u.sort_order });
      }));
      loadShifts();
    } catch (err) {
      toast.error('Reihenfolge konnte nicht gespeichert werden');
    }
  };

  return {
    dialogOpen, setDialogOpen,
    massnahmeDialogOpen, setMassnahmeDialogOpen,
    importDialogOpen, setImportDialogOpen,
    activityDialogOpen, setActivityDialogOpen,
    saving, importing,
    selectedShift, selectedMassnahme, selectedActivity,
    formData, setFormData,
    recurrence, setRecurrence,
    multiDayDays, setMultiDayDays,
    activityRecurrence, setActivityRecurrence,
    activityMultiDayDays, setActivityMultiDayDays,
    massnahmeForm, setMassnahmeForm,
    activityForm, setActivityForm,
    openNewShiftDialog, openEditShiftDialog, handleSaveShift, handleDeleteShift, handleDeleteSeries, handleShiftDrop, handleActivityDrop,
    openNewMassnahmeDialog, openEditMassnahmeDialog, handleSaveMassnahme, handleDeleteMassnahme,
    deleteMassnahmeDialog, confirmDeleteMassnahme, closeDeleteMassnahmeDialog,
    handleDragEnd, handleDownloadTemplate, handleImport,
    openActivityDialog, openEditActivityDialog, handleSaveActivity, handleDeleteActivity,
    reorderEntryInCell, reorderEntryBeforeTarget,
  };
}
